import styles from "./AppLayout.module.css";
import classNames from "classnames";
import { Trans } from "react-i18next";
import { useContext, useRef, type JSX } from "react";
import {
  AppContext,
  AppDispatchContext,
  CalculationActionTypes,
  selectPaidServices,
  selectSubsidyDependantCalculationDetails,
} from "../../state";
import { useI18n } from "../../i18n";
import { AdvantageList } from "../AdvantageList";
import { CalculatorForm } from "../CalculatorForm";
import { Container } from "../Container";
import { Content } from "../Content";
import { Heading } from "../Heading";
import { InfoBubbles, BubbleType } from "../InfoBubbles";
import { Savings } from "../Savings";
import { Warnings } from "../Warnings";
import { Hint } from "../Hint";
import { Link } from "../Link";
import { CalculationDetails } from "../CalculationDetails";
import { StickyBottomSheet, useIntersectionObserverForStickyBottomSheet } from "../StickyBottomSheet";
import { CtaButton } from "../CTA";
import { Feedback } from "../Feedback";
import { WithCookieConsent } from "../WithCookieConsent";
import { VersionInfo } from "../VersionInfo";

const AppLayout = (): JSX.Element => {
  const { t } = useI18n();
  const state = useContext(AppContext);
  const dispatch = useContext(AppDispatchContext);

  const { ref: bubbleSectionRef, shouldDisplayStickyBottomSheet } = useIntersectionObserverForStickyBottomSheet();

  const isDealerMode = state.configuration.calculatorEnv === "dealer";

  const appClasses = classNames(styles.foundation, styles.typography, styles.customVariables, styles.app);
  const layoutContainerClasses = classNames(styles.layoutContainer, { [styles.noMargins]: true });

  const handleShowResultsDetails = (showDetails: boolean): void => {
    dispatch({ type: CalculationActionTypes.SHOW_RESULTS_DETAILS, payload: showDetails });
  };

  const BUBBLE_TYPE_MONTHLY_RATE = "monthlyRate";

  const scrollTargetRefs = {
    employerPaidCostsRef: useRef<HTMLTableElement>(null),
    bikeSavingsRef: useRef<HTMLTableElement>(null),
    monthlyRateRef: useRef<HTMLDivElement>(null),
    individualSubsidyRef: useRef<HTMLInputElement>(null),
  };

  const bubbleClickHandler = (bubbleType: BubbleType) => {
    const ref = scrollTargetRefs[`${bubbleType}Ref`]?.current;

    const scrollTargetCssClasses = ["scroll-margin-top"];

    if (bubbleType === BUBBLE_TYPE_MONTHLY_RATE) {
      handleShowResultsDetails(true);

      scrollTargetCssClasses.push("highlight-fade-monthly-rate");
    } else {
      scrollTargetCssClasses.push("highlight-fade");
    }

    if (ref) {
      setTimeout(() => {
        ref.classList.add(...scrollTargetCssClasses);
        // Set focus to the element so navigation can be proceeded via keyboard
        ref.setAttribute("tabIndex", "-1");
        ref.focus();

        setTimeout(() => {
          ref.classList.remove(...scrollTargetCssClasses);
          ref.removeAttribute("tabIndex");
        }, 2500);

        ref.scrollIntoView({ behavior: "smooth" });
      }, 0);
    }
  };

  const subsidyBoxClickHandler = () => {
    const ref = scrollTargetRefs.individualSubsidyRef?.current;
    const scrollTargetCssClasses = ["scroll-margin-top"];

    if (ref) {
      setTimeout(() => {
        ref.classList.add(...scrollTargetCssClasses);

        setTimeout(() => {
          ref.classList.remove(...scrollTargetCssClasses);
        }, 2500);

        ref.scrollIntoView({ behavior: "smooth" });
        ref.focus();
      }, 0);
    }
  };

  const subsidyDependantCalculationDetails = selectSubsidyDependantCalculationDetails(state);
  const { companyPays } = selectPaidServices(state);

  return (
    <div className={appClasses}>
      <div className={layoutContainerClasses}>
        {isDealerMode && (
          <Container reducedGap fullWidth>
            <Content reducedGap>
              <Heading as="h1">
                <Trans i18nKey={"jobRadCalculator"} />
              </Heading>
              <AdvantageList list={[{ label: "allKilometersFree" }, { label: "privateUse" }, { label: "noDeposit" }]} />
            </Content>
          </Container>
        )}
        <Container increasedGap>
          <Content background="gray" withPadding>
            <CalculatorForm scrollTargetRef={scrollTargetRefs.individualSubsidyRef} />
          </Content>
        </Container>
        <Container breakOutSticky increasedGap innerRef={bubbleSectionRef}>
          <Content reducedGap>
            <InfoBubbles
              monthlyRate={subsidyDependantCalculationDetails?.netChargeEmployee}
              bikeSavings={subsidyDependantCalculationDetails?.savingsBetweenPrivateAndJobrad}
              bubbleClickHandler={bubbleClickHandler}
              companyPays={companyPays}
            />
            <Warnings warnings={state.calculation.warnings} />
            <Hint variant="centered">{t("hints.allValuesRounded")}</Hint>
          </Content>
          {!isDealerMode && (
            <Content>
              <CtaButton id="JobRadRechner_CTA_FHS"></CtaButton>
            </Content>
          )}
        </Container>
        <Container>
          <Content isCentered>
            <Heading as="h3" reducedGap className={styles.savingsSectionHeading}>
              {t("savings.sectionHeading")}
            </Heading>
            <Savings scrollTargetRefs={scrollTargetRefs} subsidyBoxClickHandler={subsidyBoxClickHandler} />
            {/*
              ESLint suppression on monthlyRateRef prop below:
              Passing refs as props is a valid React pattern (ref forwarding).
              The ref is not accessed (.current) during render - it's only passed down to
              CalculationDetails which attaches it to a DOM element. The ref is then used in
              event handlers (bubbleClickHandler) for scrolling and highlighting.
              This is a false positive from the react-hooks/refs rule.
            */}
            <CalculationDetails
              isExpanded={state.calculation.showResultsDetails}
              onToggle={handleShowResultsDetails}
              // eslint-disable-next-line react-hooks/refs
              monthlyRateRef={scrollTargetRefs.monthlyRateRef}
            />
            <Hint variant="highlighted">{t("hints.nonBindingSampleCalculation")}</Hint>
            <Hint>
              <Trans
                i18nKey={"hints.sourceTaxCalculation"}
                t={t}
                values={{ taxationYear: state.calculation.calculator?.taxationYear }}
                components={{
                  Link: <Link id="link-sourceTaxCalculation" key={"hints.sourceTaxCalculation"} target="_blank" />,
                }}
              ></Trans>
            </Hint>
            <VersionInfo />
            <WithCookieConsent>
              <Feedback />
            </WithCookieConsent>
          </Content>
        </Container>
      </div>
      <StickyBottomSheet isVisible={shouldDisplayStickyBottomSheet}>
        <InfoBubbles
          monthlyRate={subsidyDependantCalculationDetails?.netChargeEmployee}
          bikeSavings={subsidyDependantCalculationDetails?.savingsBetweenPrivateAndJobrad}
          bubbleClickHandler={bubbleClickHandler}
          companyPays={companyPays}
          variant="mini"
        />
        {state.configuration.elementVisibility.bottomSheetCtaButton && (
          <CtaButton id="JobRadRechner_BottomSheet_CTA_FHS" variant="forBottomSheet"></CtaButton>
        )}
      </StickyBottomSheet>
    </div>
  );
};

export { AppLayout };
